import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { vertexShader, fragmentShader } from './shaders';

export interface OrbInitOptions {
  bgColor?: string;
  showCord?: boolean;
}

export class OrbScene {
  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private composer!: EffectComposer;
  private mesh!: THREE.Mesh;
  private uniforms!: {
    uTime: { value: number };
    uLightDir: { value: THREE.Vector3 };
    uCameraPos: { value: THREE.Vector3 };
  };
  private rafId = 0;
  private startTime = performance.now();

  private cord: { group: THREE.Group; curve: THREE.CatmullRomCurve3 } | null = null;
  private cordParticles: Array<{ mesh: THREE.Mesh; offset: number; speed: number }> = [];

  init(canvas: HTMLCanvasElement, options: OrbInitOptions = {}): void {
    const { bgColor = '#000000', showCord = false } = options;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.setSize(w, h, false);
    // LinearToneMapping (identity at exposure=1) so bgColor renders exactly as specified
    this.renderer.toneMapping = THREE.LinearToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(bgColor);

    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    this.camera.position.set(0, 0, 4.2);

    this.uniforms = {
      uTime:      { value: 0 },
      uLightDir:  { value: new THREE.Vector3(0.6, 0.8, 1.0).normalize() },
      uCameraPos: { value: this.camera.position.clone() },
    };

    const geometry = new THREE.IcosahedronGeometry(0.9, 80);
    const material = new THREE.ShaderMaterial({ vertexShader, fragmentShader, uniforms: this.uniforms });
    this.mesh = new THREE.Mesh(geometry, material);
    this.scene.add(this.mesh);

    if (showCord) this.buildCord();

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.composer.addPass(new UnrealBloomPass(new THREE.Vector2(w, h), 0.9, 0.4, 0.20));

    this.startLoop();
  }

  private buildCord(): void {
    const group = new THREE.Group();
    this.scene.add(group);

    // Curve from orb top upward — slight S-bend for organic feel
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3( 0.00,  0.90, 0),
      new THREE.Vector3( 0.05,  1.70, 0),
      new THREE.Vector3(-0.05,  2.60, 0),
      new THREE.Vector3( 0.00,  4.40, 0),
    ]);

    // Particles — blues and greens only
    const particleColors = [
      new THREE.Color(0.20, 0.80, 1.00),  // sky blue
      new THREE.Color(0.10, 0.40, 1.00),  // electric blue
      new THREE.Color(0.00, 1.00, 0.65),  // cyan-green
      new THREE.Color(0.00, 0.75, 1.00),  // cyan
      new THREE.Color(0.20, 1.00, 0.50),  // mint green
      new THREE.Color(0.05, 0.55, 0.95),  // ocean blue
    ];

    particleColors.forEach((color, i) => {
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.028, 8, 8),
        new THREE.MeshBasicMaterial({ color }),
      );
      group.add(mesh);
      this.cordParticles.push({
        mesh,
        offset: i / particleColors.length,
        speed: 0.22 + (i % 3) * 0.07,
      });
    });

    this.cord = { group, curve };
  }

  setMousePos(ndcX: number, ndcY: number): void {
    const x = ndcX * 0.8;
    const y = ndcY * 0.8;
    this.uniforms.uLightDir.value.set(x, y, 1.0).normalize();
  }

  resize(w: number, h: number): void {
    if (!this.renderer) return;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
    this.composer.setSize(w, h);
  }

  dispose(): void {
    cancelAnimationFrame(this.rafId);
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.ShaderMaterial).dispose();
    if (this.cord) {
      this.cord.group.traverse(child => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
    }
    this.renderer.dispose();
  }

  private startLoop(): void {
    const tick = () => {
      this.rafId = requestAnimationFrame(tick);

      const elapsed = (performance.now() - this.startTime) * 0.001;
      this.uniforms.uTime.value = elapsed;

      this.mesh.position.y = Math.sin(elapsed * 0.45) * 0.08;
      this.mesh.rotation.y = elapsed * 0.06;
      this.mesh.rotation.x = Math.sin(elapsed * 0.3) * 0.04;

      // Cord group bobs with the orb
      if (this.cord) {
        this.cord.group.position.y = this.mesh.position.y;
        this.cordParticles.forEach(p => {
          const t = (elapsed * p.speed + p.offset) % 1.0;
          p.mesh.position.copy(this.cord!.curve.getPoint(t));
        });
      }

      this.composer.render();
    };
    tick();
  }
}
